import { apm } from "@elastic/apm-rum";
import { CaptureSystem } from "@inovitas/infra3dsdk/dist/types/mm/api/utils/capturesystem";
import { BlurOn, FiberManualRecord } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";

import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { CockpitArrow } from "shared/common/utils/icons/icons";
import { CameraNode } from "shared/ui/cockpit/Cockpit";
import IceCreamSwitcher from "./IceCreamSwitcher";

interface props {
  camerasArray: CameraNode[];
  activeCamera: CaptureSystem | undefined;
}

export default function CameraPositions({ camerasArray, activeCamera }: props) {
  const viewer = defaultStore((state) => state.viewer);
  const cameraSliderId = defaultStore((state) => state.cameraSliderId);
  const changeCameraSliderId = defaultStore(
    (state) => state.changeCameraSliderId
  );

  const arrow = <CockpitArrow />;

  useEffect(() => {
    if (activeCamera?.sensorgroup === 4 && cameraSliderId === 0) {
      changeCameraSliderId(1);
    }
  }, []);

  const [cameraOne, setCameraOne] = useState<CameraNode[]>([]);
  const [cameraTwo, setCameraTwo] = useState<CameraNode[]>([]);
  const [cameraThree, setCameraThree] = useState<CameraNode[]>([]);
  const [cameraFour, setCameraFour] = useState<CameraNode[]>([]);
  const [cameraFive, setCameraFive] = useState<CameraNode[]>([]);
  const [cameraSix, setCameraSix] = useState<CameraNode[]>([]);
  const [cameraSeven, setCameraSeven] = useState<CameraNode[]>([]);
  const [cameraEight, setCameraEight] = useState<CameraNode[]>([]);
  const [sliderCameras, setSliderCameras] = useState<CameraNode[]>([]);

  const [hoverFrontCamera, setHoverFrontCamera] = useState<boolean>(false);
  const [hoverCircleCamera, setHoverCircleCamera] = useState<boolean>(false);

  //refresh and set possible cameras in their position everytime cameras list is changing
  useEffect(() => {
    if (camerasArray.length > 0) {
      const arr1: CameraNode[] = [];
      const arr2: CameraNode[] = [];
      const arr3: CameraNode[] = [];
      const arr4: CameraNode[] = [];
      const arr5: CameraNode[] = [];
      const arr6: CameraNode[] = [];
      const arr7: CameraNode[] = [];
      const arr8: CameraNode[] = [];
      const circle: CameraNode[] = [];
      camerasArray.forEach((item) => {
        if (item.frame !== null) {
          if (
            item.frame.streamtype === "cubemap" ||
            item.frame.streamtype === "calotte"
          ) {
            if (circle.length < 2) {
              if (circle.length === 1) {
                if (circle[0].frame.capturesystem.sensorgroup === 3) {
                  circle.push(item);
                } else {
                  circle.unshift(item);
                }
              } else {
                circle.push(item);
              }
            }
          } else {
            switch (item.frame.capturesystem.orientation) {
              case 0:
                arr1.push(item);
                break;
              case 45:
                arr2.push(item);
                break;
              case 90:
                arr3.push(item);
                break;
              case 135:
                arr4.push(item);
                break;
              case 180:
                arr5.push(item);
                break;
              case -135:
                arr6.push(item);
                break;
              case -90:
                arr7.push(item);
                break;
              case -45:
                arr8.push(item);
                break;
            }
          }
        }
      });
      let arrColor: CameraNode | undefined;
      let arrGrey: CameraNode | undefined;
      arr1.forEach((item) => {
        if (
          arrColor === undefined &&
          item.frame.capturesystem.sensorgroup === 1
        ) {
          arrColor = item;
        }
        if (
          arrGrey === undefined &&
          item.frame.capturesystem.sensorgroup === 2
        ) {
          arrGrey = item;
        }
      });
      if (arrColor && !arrGrey) {
        setCameraOne([arrColor]);
      } else if (!arrColor && arrGrey) {
        setCameraOne([arrGrey]);
      } else if (arrColor && arrGrey) {
        setCameraOne([arrColor, arrGrey]);
      } else {
        setCameraOne([]);
      }
      setCameraTwo(arr2);
      setCameraThree(arr3);
      setCameraFour(arr4);
      setCameraFive(arr5);
      setCameraSix(arr6);
      setCameraSeven(arr7);
      setCameraEight(arr8);
      setSliderCameras(circle);
    }
  }, [camerasArray]);

  //change view to camera with id
  const handleChangeCamera = (id: string) => {
    if (viewer) {
      viewer.moveToCamera(id);
    }
  };

  //change pano cameras
  const circleCameraSlider = () => {
    const activeCameraIsInCircle =
      sliderCameras.filter(
        (camera) => camera.frame.capturesystemID === activeCamera?.id
      ).length > 0;

    if (activeCameraIsInCircle && sliderCameras.length > 1) {
      const newCameraSliderId = cameraSliderId === 0 ? 1 : 0;
      handleChangeCamera(
        sliderCameras[newCameraSliderId].frame.capturesystemID
      );
      changeCameraSliderId(newCameraSliderId);
    } else {
      handleChangeCamera(sliderCameras[cameraSliderId].frame.capturesystemID);
    }
  };

  // change cameraSliderId everytime when pano cameras are changed by users
  useEffect(() => {
    if (sliderCameras.length === 2 && cameraSliderId == 1) {
      changeCameraSliderId(1);
    } else if (sliderCameras.length === 1 && cameraSliderId == 1) {
      changeCameraSliderId(0);
    }
  }, [sliderCameras]);

  return (
    <Box
      width={"150px"}
      height={"150px"}
      display={camerasArray.length > 0 ? "flex" : "none"}
      justifyContent={"center"}
      alignItems={"center"}
    >
      <Box width={"100%"} height="100%" position={"relative"}>
        {cameraOne.length === 1 && (
          <Box
            position={"absolute"}
            zIndex={1}
            top="calc(15% - 2px)"
            left="calc(50% - 15px)"
            sx={{
              color:
                activeCamera?.id === cameraOne[0].frame.capturesystemID
                  ? "primary.main"
                  : "white",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main", cursor: "pointer" },
              },
            }}
            onClick={() =>
              monitor(apm, "selected-camera-one | Sensor", () =>
                handleChangeCamera(cameraOne[0].frame.capturesystemID)
              )
            }
          >
            {arrow}
          </Box>
        )}
        {cameraOne.length > 1 && (
          <Box
            position={"absolute"}
            zIndex={1}
            top="calc(15% - 2px)"
            left="calc(50% - 15px)"
            sx={{
              color:
                activeCamera?.id === cameraOne[0].frame.capturesystemID ||
                activeCamera?.id === cameraOne[1].frame.capturesystemID
                  ? "primary.main"
                  : "white",
              ":hover": { color: "secondary.main", cursor: "pointer" },
            }}
            onMouseOver={() => setHoverFrontCamera(true)}
            onMouseOut={() => setHoverFrontCamera(false)}
          >
            {arrow}
            {hoverFrontCamera && (
              <IceCreamSwitcher
                cameras={cameraOne}
                activeCamera={activeCamera}
                setHoverFrontCamera={setHoverFrontCamera}
              />
            )}
          </Box>
        )}
        {cameraTwo.length > 0 && (
          <Box
            position={"absolute"}
            zIndex={1}
            top="calc(35% - 18px)"
            left="calc(60% + 2px)"
            sx={{
              color:
                activeCamera?.id === cameraTwo[0].frame.capturesystemID
                  ? "primary.main"
                  : "white",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main", cursor: "pointer" },
              },
              rotate: "45deg",
            }}
            onClick={() =>
              monitor(apm, "selected-camera-two | Sensor", () =>
                handleChangeCamera(cameraTwo[0].frame.capturesystemID)
              )
            }
          >
            {arrow}
          </Box>
        )}
        {cameraThree.length > 0 && (
          <Box
            position={"absolute"}
            zIndex={1}
            top="calc(55% - 15px)"
            left="70%"
            sx={{
              color:
                activeCamera?.id === cameraThree[0].frame.capturesystemID
                  ? "primary.main"
                  : "white",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main", cursor: "pointer" },
              },
              rotate: "90deg",
            }}
            onClick={() =>
              monitor(apm, "selected-camera-three | Sensor", () =>
                handleChangeCamera(cameraThree[0].frame.capturesystemID)
              )
            }
          >
            {arrow}
          </Box>
        )}
        {cameraFour.length > 0 && (
          <Box
            position={"absolute"}
            zIndex={1}
            bottom="calc(25% - 17px)"
            right="calc(30% - 18px)"
            sx={{
              color:
                activeCamera?.id === cameraFour[0].frame.capturesystemID
                  ? "primary.main"
                  : "white",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main", cursor: "pointer" },
              },
              rotate: "135deg",
            }}
            onClick={() =>
              monitor(apm, "selected-camera-four | Sensor", () =>
                handleChangeCamera(cameraFour[0].frame.capturesystemID)
              )
            }
          >
            {arrow}
          </Box>
        )}
        {cameraFive.length > 0 && (
          <Box
            position={"absolute"}
            zIndex={1}
            bottom="calc(5% - 1px)"
            left="calc(50% - 15px)"
            sx={{
              color:
                activeCamera?.id === cameraFive[0].frame.capturesystemID
                  ? "primary.main"
                  : "white",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main", cursor: "pointer" },
              },
              rotate: "180deg",
            }}
            onClick={() =>
              monitor(apm, "selected-camera-five | Sensor", () =>
                handleChangeCamera(cameraFive[0].frame.capturesystemID)
              )
            }
          >
            {arrow}
          </Box>
        )}
        {cameraSix.length > 0 && (
          <Box
            position={"absolute"}
            zIndex={1}
            bottom="calc(25% - 18px)"
            left="calc(30% - 18px)"
            sx={{
              color:
                activeCamera?.id === cameraSix[0].frame.capturesystemID
                  ? "primary.main"
                  : "white",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main", cursor: "pointer" },
              },
              rotate: "-135deg",
            }}
            onClick={() =>
              monitor(apm, "selected-camera-six | Sensor", () =>
                handleChangeCamera(cameraSix[0].frame.capturesystemID)
              )
            }
          >
            {arrow}
          </Box>
        )}
        {cameraSeven.length > 0 && (
          <Box
            position={"absolute"}
            zIndex={1}
            top="calc(55% - 15px)"
            right="calc(70% + 1px)"
            sx={{
              color:
                activeCamera?.id === cameraSeven[0].frame.capturesystemID
                  ? "primary.main"
                  : "white",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main", cursor: "pointer" },
              },
              rotate: "-90deg",
            }}
            onClick={() =>
              monitor(apm, "selected-camera-seven | Sensor", () =>
                handleChangeCamera(cameraSeven[0].frame.capturesystemID)
              )
            }
          >
            {arrow}
          </Box>
        )}
        {cameraEight.length > 0 && (
          <Box
            position={"absolute"}
            zIndex={1}
            top="calc(35% - 18px)"
            right="calc(60% + 4px)"
            sx={{
              color:
                activeCamera?.id === cameraEight[0].frame.capturesystemID
                  ? "primary.main"
                  : "white",
              "@media (hover: hover)": {
                ":hover": { color: "secondary.main", cursor: "pointer" },
              },
              rotate: "-45deg",
            }}
            onClick={() =>
              monitor(apm, "selected-camera-eight | Sensor", () =>
                handleChangeCamera(cameraEight[0].frame.capturesystemID)
              )
            }
          >
            {arrow}
          </Box>
        )}
        <Box
          width="40%"
          height="40%"
          position={"absolute"}
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          gap="2px"
          top="calc(35% - 13px)"
          left="calc(30% - 13px)"
          padding={"6px"}
          sx={{ borderRadius: "50%", border: "7px white solid" }}
        >
          {sliderCameras.length > 0 && (
            <Box
              width={"calc(100% - 8px)"}
              position={"relative"}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"center"}
              overflow={"hidden"}
              alignItems={"center"}
              padding={"2px"}
              sx={{
                cursor: "pointer",
                aspectRatio: "1 / 1",
                color:
                  sliderCameras.filter(
                    (camera) =>
                      camera.frame.capturesystemID === activeCamera?.id
                  ).length > 0
                    ? "white"
                    : "darkblue.main",
                borderRadius: "50%",
                backgroundColor:
                  sliderCameras.filter(
                    (camera) =>
                      camera.frame.capturesystemID === activeCamera?.id
                  ).length > 0
                    ? "primary.main"
                    : "white",
                "@media (hover: hover)": {
                  ":hover": {
                    backgroundColor: "secondary.main",
                    color: "white",
                  },
                },
              }}
              onMouseOver={() => setHoverCircleCamera(true)}
              onMouseOut={() => setHoverCircleCamera(false)}
              onClick={() =>
                monitor(apm, "selected-camera-circle | Sensor", () =>
                  circleCameraSlider()
                )
              }
            >
              <Box
                position={"absolute"}
                width="100%"
                height="70%"
                alignItems={"end"}
                display={"flex"}
                top="0"
                left={
                  cameraSliderId == 0 || sliderCameras.length === 0
                    ? "0%"
                    : "-100%"
                }
                sx={{ transition: "left 0.2s" }}
              >
                {sliderCameras.map(
                  (camera) =>
                    camera.frame.capturesystem.sensorgroup === 3 && (
                      <Typography
                        key={camera.frame.capturesystemID}
                        textAlign={"center"}
                        variant="body2"
                        fontWeight={600}
                        width={"100%"}
                        paddingBottom={sliderCameras.length > 1 ? "3px" : "0px"}
                        minWidth={"100%"}
                        sx={{ userSelect: "none" }}
                      >
                        360°
                      </Typography>
                    )
                )}
                {sliderCameras.map(
                  (camera) =>
                    camera.frame.capturesystem.sensorgroup === 4 && (
                      <BlurOn
                        key={camera.frame.capturesystemID}
                        width={"100%"}
                        fontSize="medium"
                        sx={{
                          paddingBottom: "3px",
                          minWidth: "100%",
                          color:
                            sliderCameras.filter(
                              (camera) =>
                                camera.frame.capturesystemID ===
                                activeCamera?.id
                            ).length > 0 || hoverCircleCamera
                              ? "white"
                              : "darkblue.main",
                        }}
                      />
                    )
                )}
              </Box>
              {sliderCameras.length > 1 && (
                <Box
                  position="absolute"
                  height="30%"
                  width="100%"
                  justifyContent={"center"}
                  alignItems={"start"}
                  bottom={0}
                  display={"flex"}
                  gap={"4px"}
                >
                  <FiberManualRecord
                    sx={{
                      fontSize: "8px",
                      cursor: "pointer",
                      borderRadius: "50%",
                      border: "2px transparent solid",
                      borderColor:
                        activeCamera?.id ===
                        sliderCameras[0].frame.capturesystemID
                          ? "white"
                          : "transparent",
                      transition: "border 0.15s",
                    }}
                  />
                  <FiberManualRecord
                    sx={{
                      fontSize: "8px",
                      cursor: "pointer",
                      borderRadius: "50%",
                      border: "2px transparent solid",
                      borderColor:
                        activeCamera?.id ===
                        sliderCameras[1].frame.capturesystemID
                          ? "white"
                          : "transparent",
                      transition: "border 0.15s",
                    }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
